import type {
  CentinelaMessageTone,
  CentinelaSchedule,
} from "../../features/centinela/types";

export const WATCH_STATUSES = [
  "safe",
  "warning",
  "alert",
  "risk",
  "offline",
  "sin_custodia",
] as const;

export type WatchStatus = (typeof WATCH_STATUSES)[number];

export const CALL_SEVERITIES = ["high", "medium", "low", "info"] as const;

export type CallSeverity = (typeof CALL_SEVERITIES)[number];

export const CALL_EVENT_TYPES = [
  "route_deviation",
  "unauthorized_stop",
  "danger_zone",
  "speeding",
  "routine",
  "suspicious_stop",
  "gps_loss",
  "jamming",
  "delivery_confirmation",
] as const;

export type CallEventType = (typeof CALL_EVENT_TYPES)[number];

export const CALL_OUTCOMES = ["confirmed", "false_alarm", "escalated", "no_answer"] as const;

export type CallOutcome = (typeof CALL_OUTCOMES)[number];

export const PROTOCOL_ACTIONS = [
  "call_operator",
  "validate_keyword",
  "notify_supervisor",
  "notify_client",
  "activate_geofence",
  "request_photo",
  "escalate_incident",
  "log_incident",
] as const;

export type ProtocolActionId = (typeof PROTOCOL_ACTIONS)[number];

export interface MonitoringSummary {
  watchedUnits: number;
  incidentUnitName: string | null;
  incidentNote: string | null;
  activeGeofences: number;
  escortedTrips: number;
  generatedAt: string;
}

export type FleetStatusBreakdown = Record<WatchStatus, number>;

export interface ConversationMessage {
  id: string;
  role: "sentinel" | "operator";
  text: string;
  at: string;
}

export interface SentinelCall {
  id: string;
  unitId: string;
  unitName: string;
  type: CallEventType;
  outcome: CallOutcome;
  startedAt: string;
  durationSeconds: number;
  transcript: ConversationMessage[];
}

export interface ProtocolStep {
  id: string;
  actionId: ProtocolActionId;
  delay: string;
}

export interface Protocol {
  id: string;
  name: string;
  trigger: string;
  enabled: boolean;
  steps: ProtocolStep[];
}

export const PROTOCOL_STEP_STATUSES = ["done", "failed", "skipped", "pending"] as const;

export type ProtocolStepStatus = (typeof PROTOCOL_STEP_STATUSES)[number];

export const PROTOCOL_CHANNELS = [
  "screen",
  "sms",
  "email",
  "call",
  "whatsapp",
  "push",
  "internal",
] as const;

export type ProtocolChannel = (typeof PROTOCOL_CHANNELS)[number];

export const PROTOCOL_EVENT_STATUSES = [
  "completed",
  "inProgress",
  "partialFailure",
  "cancelled",
] as const;

export type ProtocolEventStatus = (typeof PROTOCOL_EVENT_STATUSES)[number];

export interface ProtocolStepResult {
  stepIndex: number;
  actionId: ProtocolActionId;
  channel: ProtocolChannel;
  status: ProtocolStepStatus;
  at: string | null;
  detail: string | null;
  recipientName: string | null;
  recipientRole: string | null;
}

export interface ProtocolEvent {
  id: string;
  protocolName: string;
  protocolIcon: string;
  unitId: string;
  status: ProtocolEventStatus;
  trigger: string;
  startedAt: string;
  steps: ProtocolStepResult[];
}

/**
 * Un tipo de evento de cámara con lo que se sabe de él en la ventana.
 *
 * Reemplaza a los tres indicadores fijos —somnolencia, distracción, vista al
 * frente—, que dependían de nt-28, nt-29 y nt-30: tipos de cabina que hoy no
 * están mapeados, así que esas casillas decían «sin datos» de forma permanente.
 * Tres huecos que nunca se llenan enseñan menos que una lista de lo que sí
 * llegó.
 */
export interface CameraEventSummary {
  kind: string;
  label: string;
  total: number;
  /** ISO del más reciente de ese tipo. */
  ultimo: string;
}

/**
 * Un evento de cámara, venga de donde venga.
 *
 * `origen` importa porque las dos fuentes son distintas de verdad: un `caso` es
 * algo que Centinela abrió y atendió; una entrada de `bitacora` es un hecho que
 * se anotó y nada más, porque su tipo es de solo registro. Mezclarlas sin
 * distinguir haría parecer que nadie atendió eventos que nunca lo necesitaron.
 */
export interface CameraEvent {
  id: string;
  kind: string;
  at: string;
  label: string;
  urgency: string;
  origen: "caso" | "bitacora";
}

export const CHIP_TONES = ["success", "info", "warning", "error", "neutral"] as const;

export type ChipTone = (typeof CHIP_TONES)[number];

export interface UnitChip {
  label: string;
  tone: ChipTone;
}

export interface UnitWatchState {
  unitId: string;
  deviceId: string | null;
  status: WatchStatus;
  hasOperatorPhone: boolean;
  underCustody: boolean;
  openCaseId: string | null;
  isCalling: boolean;
  address: string | null;
  aiMessageType: CentinelaMessageTone;
  speedKph: number | null;
  /** Rumbo en grados del último reporte; `null` si el equipo no lo manda. */
  headingDeg: number | null;
  /** Foto del expediente del conductor. */
  driverPhoto: string | null;
  fuelPercent: number;
  updatedAt: string | null;
  position: { lat: number; lng: number } | null;
  aiMessage: string;
  chips: UnitChip[];
}

export const GEOFENCE_SUGGESTION_CONFIDENCES = ["baja", "media", "alta"] as const;

export type GeofenceSuggestionConfidence =
  (typeof GEOFENCE_SUGGESTION_CONFIDENCES)[number];

export interface GeofenceSuggestionSource {
  url: string;
  title: string;
}

export interface GeofenceSuggestion {
  id: string;
  name: string;
  reason: string;
  type: string;
  color: string;
  origin: string | null;
  confidence: GeofenceSuggestionConfidence | null;
  confidenceRaw: string | null;
  sources: GeofenceSuggestionSource[];
  distanceToRouteKm: number | null;
  lat: number | null;
  lng: number | null;
  radiusMeters: number | null;
}

export const ROUTE_STOP_KINDS = ["origin", "stop", "destination"] as const;

export type RouteStopKind = (typeof ROUTE_STOP_KINDS)[number];

export interface RouteStop {
  name: string;
  kind: RouteStopKind;
}

export interface RouteInsight {
  routeId: string;
  riskScore: number;
  findings: string[];
  safetyScore: number;
  verificationIntervalMin: number;
  unsafeZones: number;
  departureWindow: string;
  usedInTrips: number;
  createdByAi: boolean;
  durationMin: number;
  stops: RouteStop[];
}

export const TRIP_CUSTODY_STATES = ["active", "scheduled", "none"] as const;

export type TripCustodyState = (typeof TRIP_CUSTODY_STATES)[number];

export interface TripWatchState {
  tripId: string;
  custody: TripCustodyState;
  calls: number;
  incidents: number;
  progressPercent: number;
  unitName: string;
  driverName: string;
  departureAt: string;
  estimatedArrivalAt: string;
  actualArrivalAt: string | null;
  nextCheckAt: string | null;
  note: string | null;
  createdByAi: boolean;
}

export const CALL_PURPOSES = ["routineAndCustody", "alerts", "deliveries"] as const;

export type CallPurpose = (typeof CALL_PURPOSES)[number];

export type CallPurposeBreakdown = Record<CallPurpose, number>;

export interface TripCallUsage {
  tripId: string;
  tripName: string;
  callsUsed: number;
}

export const PERIOD_DETAIL_TAGS = ["included", "limited", "cost"] as const;

export type PeriodDetailTag = (typeof PERIOD_DETAIL_TAGS)[number];

export interface PeriodDetailItem {
  id: string;
  icon: string;
  tone: ChipTone;
  count: number;
  tag: PeriodDetailTag;
  costLabel: string | null;
}

export interface AddonOption {
  id: string;
  name: string;
  description: string;
  icon: string;
  quantity: number;
  unitLabelKey: string;
  periodKey: string;
  price: number;
  highlight: boolean;
}

export interface UsageData {
  planName: string;
  planPrice: number;
  renewalDate: string;
  unitsIncluded: number;
  geofencesIncluded: number;
  callsIncluded: number;
  callsUsed: number;
  extraCallPrice: number;
  projectionText: string;
  periodStart: string;
  periodEnd: string;
  purposes: CallPurposeBreakdown;
  callsByType: { type: CallEventType; count: number }[];
  tripCalls: TripCallUsage[];
  averageCallsPerTrip: number;
  periodDetails: PeriodDetailItem[];
  addons: AddonOption[];
}

export interface NotificationChannelOption {
  id: string;
  label: string;
  icon: string;
  selected: boolean;
  available: boolean;
  requires: string | null;
  unavailableReason: string | null;
}

export interface NotificationUser {
  userId: string;
  name: string;
  email: string | null;
  phone: string | null;
  roleId: string | null;
  roleName: string | null;
  active: boolean;
  enabled: boolean;
  noPhoneReason: string | null;
  channels: NotificationChannelOption[];
  types: string[];
  schedule: CentinelaSchedule | null;
  onShift: boolean | null;
  shiftReason: string | null;
}

export const SENTINEL_BEHAVIORS = [
  "autoCall",
  "validateKeyword",
  "silentProtocol",
  "escalateSupervisor",
  "notifyClient",
  "dailyReport",
  "routeAdjust",
] as const;

export type SentinelBehaviorId = (typeof SENTINEL_BEHAVIORS)[number];

export const ALERT_TRIGGERS = [
  "engineBlock",
  "engineOn",
  "engineOff",
  "speeding",
  "signalLoss",
  "unauthorizedStop",
  "noAnswer",
  "fleetReport",
  "tripStart",
  "tripEnd",
] as const;

export type AlertTriggerId = (typeof ALERT_TRIGGERS)[number];

export const THRESHOLD_UNITS = ["kmh", "min", "hour"] as const;

export type ThresholdUnit = (typeof THRESHOLD_UNITS)[number];

export const THRESHOLD_OPTIONS = {
  kmh: [60, 80, 90, 100, 110, 120, 150],
  min: [5, 10, 15, 20, 30, 45, 60],
  hour: [1, 2, 3, 4, 6, 8, 12, 24],
} as const satisfies Record<ThresholdUnit, readonly number[]>;

export interface AlertTrigger {
  id: AlertTriggerId;
  icon: string;
  enabled: boolean;
  thresholdUnit: ThresholdUnit | null;
  thresholdValue: number | null;
  retryMinutes: number | null;
  maxAttempts: number | null;
}

export interface UnitCallConfig {
  unitId: string;
  unitName: string;
  driverName: string;
  operatorPhone: string;
  keyword: string;
  enabled: boolean;
}

export interface SentinelSettings {
  enabled: boolean;
  scheduleEnabled: boolean;
  activeDays: string[];
  fromTime: string;
  toTime: string;
  routineCallHours: number;
}

export interface TestCallInput {
  type: CallEventType;
  phone: string;
  operatorName: string;
}

export interface TestCallResult {
  ok: boolean;
  simulated: boolean;
  error?: string;
}

export const CHAT_THREAD_GROUPS = ["today", "yesterday", "earlier"] as const;

export type ChatThreadGroup = (typeof CHAT_THREAD_GROUPS)[number];

export interface ChatThreadSummary {
  id: string;
  title: string;
  preview: string;
  group: ChatThreadGroup;
  icon: string;
}
